import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins } from "lucide-react";
import { API, errText } from "@/lib/auth";
import { money } from "@/components/market/Shared";
import { RealSwitch } from "./RealSwitch";

const SIG = { BUY: "text-emerald-300", SELL: "text-red-300", HOLD: "text-slate-300", NO_DATA: "text-slate-500" };

const Balances = ({ a }) => !a.connected ? (
  <div className="rounded-xl border border-dashed border-[var(--line)] p-3 text-xs text-slate-400" data-testid="cb-not-connected">
    Not connected. {a.reason} Create a key at Coinbase Developer Platform → Secret API Keys (Advanced Trade, View + Trade permissions) and send me the key name and private key.</div>
) : (
  <div className="flex flex-wrap gap-2 text-xs font-num" data-testid="cb-balances">
    {a.balances.length === 0 ? <span className="text-slate-400">No balances</span> : a.balances.map((b) => (
      <span key={b.currency} className="rounded-full border border-[var(--line)] px-3 py-1">{b.currency} {b.available.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>))}
  </div>
);

export const CoinbaseCard = () => {
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/coinbase/state`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);
  const set = async (body) => {
    try { await axios.post(`${API}/coinbase/config`, body); toast.success("Saved"); load(); } catch (e) { toast.error(errText(e)); }
  };
  if (!d) return null;
  const on = d.config.real;
  return (
    <section className={`glass p-5 space-y-4 ${on ? "ring-1 ring-amber-400/50" : ""}`} data-testid="coinbase-card">
      <div className="flex items-start justify-between gap-3">
        <div><h2 className="font-head font-bold text-lg flex items-center gap-2"><Coins className="w-5 h-5 text-[var(--cyan)]" />Coinbase spot bot</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">{d.rule}</p></div>
        <RealSwitch on={on} onChange={(v, confirm) => set({ real: v, confirm })} testid="cb-real-switch" what="The Coinbase bot" />
      </div>
      <Balances a={d.account} />
      <div className="grid sm:grid-cols-3 gap-2" data-testid="cb-signals">
        {Object.entries(d.signals).map(([p, s]) => (
          <div key={p} className="rounded-xl bg-white/[0.03] border border-[var(--line)] p-3 text-xs">
            <div className="flex justify-between font-semibold"><span>{p}</span><span className={SIG[s.signal]} data-testid={`cb-signal-${p}`}>{s.signal}</span></div>
            <div className="text-slate-400 mt-1">{s.why || "Waiting for candle data"}</div></div>))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-num">
        {[["Mode", d.mode === "real" ? "REAL" : "Paper"], ["Paper cash", d.paper ? money(d.paper.cash, 2) : "—"], ["Paper value", d.paper ? money(d.paper.equity, 2) : "—"],
          ["Realized P&L", `${money(d.stats.realized_pnl, 2)} (${d.stats.wins}/${d.stats.closed} wins)`]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-white/[0.03] border border-[var(--line)] p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="mt-1 text-sm">{v}</div></div>))}
      </div>
      {d.trades.length > 0 && <div className="divide-y divide-[var(--line)] text-sm" data-testid="cb-trades">
        {d.trades.slice(0, 10).map((t) => (
          <div key={t._id} className="py-2 flex justify-between gap-3">
            <div className="min-w-0"><div>{t.product} · {t.status}{t.exit_reason ? ` (${t.exit_reason.replace("_", " ")})` : ""}</div>
              <div className="text-[11px] text-slate-500 font-num">{t.qty.toPrecision(4)} @ {money(t.entry, 2)} · cost {money(t.cost, 2)}</div></div>
            <div className={`font-num text-xs ${(t.pnl ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{t.pnl == null ? "—" : money(t.pnl, 2)}</div>
          </div>))}
      </div>}
      {d.last_error && <p className="text-xs text-amber-200" data-testid="cb-error">{d.last_error}</p>}
      {d.mode === "paper" && <button onClick={() => window.confirm("Reset Coinbase paper account to $1,000?") && axios.post(`${API}/coinbase/paper/reset`).then(load)} className="text-xs text-slate-500 underline" data-testid="cb-paper-reset">Reset paper account</button>}
    </section>
  );
};
